import React, {useEffect, useState} from 'react'
import axios from 'axios'
import {useNavigate} from 'react-router-dom'

const API_URL = process.env.REACT_APP_API_URL
const GET_SEMESTERS_URL = `${API_URL}/Custom/GetSemesters`
const GET_FACULTIES_URL = `${API_URL}/Custom/GetFaculties`
const GET_DEPARTMENTS_URL = `${API_URL}/Custom/GetDepartmentsByFaculty`
const GET_SCHEDULE_BY_CLASS_URL = `${API_URL}/Custom/GetUniversityWeeklyScheduleByClass`

interface Option {
  id: number
  name: string
  isActive?: boolean
}

interface ScheduleItem {
  id: number
  day: number
  startTime: string
  endTime: string
  courseName: string
  courseCode?: string
  classroomName: string
  name?: string
  detail?: string
}

const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar']
const dayColors = [
  'bg-primary',
  'bg-secondary',
  'bg-success',
  'bg-danger',
  'bg-warning',
  'bg-info',
  'bg-dark',
]

const PublicUniversityCourseScheduleList: React.FC = () => {
  const navigate = useNavigate()
  const [selectedSemester, setSelectedSemester] = useState<number | null>(null)
  const [semesterName, setSemesterName] = useState<string>('')
  const [faculties, setFaculties] = useState<Option[]>([])
  const [selectedFaculty, setSelectedFaculty] = useState<number | null>(null)
  const [departments, setDepartments] = useState<Option[]>([])
  const [selectedDepartment, setSelectedDepartment] = useState<number | null>(null)
  const [selectedGrade, setSelectedGrade] = useState<number | null>(null)
  const [schedule, setSchedule] = useState<ScheduleItem[]>([])

  useEffect(() => {
    axios
      .get(GET_SEMESTERS_URL)
      .then((res) => {
        const data: Option[] = res.data || []
        const active = data.find((s) => s.isActive) || data[0]
        if (active) {
          setSelectedSemester(active.id)
          setSemesterName(active.name)
        }
      })
      .catch(console.error)
  }, [])

  useEffect(() => {
    if (!selectedSemester) return
    axios
      .get(GET_FACULTIES_URL)
      .then((res) =>
        setFaculties(
          (res.data || []).filter((f: Option) => f.name !== 'Tıp Fakültesi')
        )
      )
      .catch(console.error)
    setSelectedFaculty(null)
    setDepartments([])
    setSelectedDepartment(null)
    setSelectedGrade(null)
    setSchedule([])
  }, [selectedSemester])

  useEffect(() => {
    if (!selectedFaculty) {
      setDepartments([])
      setSelectedDepartment(null)
      setSelectedGrade(null)
      setSchedule([])
      return
    }
    axios
      .get(`${GET_DEPARTMENTS_URL}/${selectedFaculty}`)
      .then((res) => setDepartments(res.data || []))
      .catch(console.error)
    setSelectedDepartment(null)
    setSelectedGrade(null)
    setSchedule([])
  }, [selectedFaculty])

  useEffect(() => {
    setSelectedGrade(null)
    setSchedule([])
  }, [selectedDepartment])

  const loadSchedule = () => {
    if (!selectedSemester || !selectedFaculty || !selectedDepartment || !selectedGrade) return
    axios
      .get(
        `${GET_SCHEDULE_BY_CLASS_URL}?semesterId=${selectedSemester}&classId=${selectedGrade}&departmentId=${selectedDepartment}`
      )
      .then((res) => setSchedule(res.data || []))
      .catch(console.error)
  }

  const renderTable = () => {
    if (!schedule.length) return null
    const grouped = schedule.reduce((acc: Record<number, ScheduleItem[]>, item) => {
      if (!acc[item.day]) acc[item.day] = []
      acc[item.day].push(item)
      return acc
    }, {})
    return (
      <div className='row'>
        {Object.entries(grouped).map(([day, items]) => (
          <div key={day} className='col-md-6 col-lg-4 mb-4'>
            <h5 className={`p-2 rounded text-center text-white ${dayColors[Number(day) - 1]}`}>
              {dayNames[Number(day) - 1]}
            </h5>
            <table className='table table-bordered table-hover'>
              <thead className='table-light'>
                <tr>
                  <th className='text-center'>Saat</th>
                  <th>Ders</th>
                  <th>Tür</th>
                  <th>Sınıf</th>
                  <th>Açıklama</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id}>
                    <td className='text-center'>
                      <span className='badge bg-info'>
                        {i.startTime} - {i.endTime}
                      </span>
                    </td>
                    <td>{i.courseCode ? `${i.courseName} (${i.courseCode})` : i.courseName}</td>
                    <td>{i.name ? i.name.split(' - ').pop() : ''}</td>
                    <td>{i.classroomName}</td>
                    <td>{i.detail || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className='container mt-4'>
      <h2 className='mb-4'>
        {semesterName ? `${semesterName} Dönemi Haftalık Ders Programı` : 'Haftalık Ders Programı'}
      </h2>
      <div className='alert alert-primary'>
        Ortak derslere rektörlük öğrenci işleri seçerek ulaşmanız gerekmektedir. OSD kodlu derslerde sınıfınız 1 olmasa dahi sınıf seçimini 1 olarak yapınız.
      </div>
      {faculties.length > 0 && (
        <div className='mb-3'>
          <label>Fakülte</label>
          <select
            className='form-control'
            value={selectedFaculty || ''}
            onChange={(e) => setSelectedFaculty(Number(e.target.value))}
          >
            <option value='' disabled>
              Seçiniz
            </option>
            {faculties.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {departments.length > 0 && (
        <div className='mb-3'>
          <label>Bölüm</label>
          <select
            className='form-control'
            value={selectedDepartment || ''}
            onChange={(e) => setSelectedDepartment(Number(e.target.value))}
          >
            <option value='' disabled>
              Seçiniz
            </option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className='mb-3'>
        <label>Sınıf</label>
        <select
          className='form-control'
          value={selectedGrade || ''}
          onChange={(e) => setSelectedGrade(Number(e.target.value))}
        >
          <option value='' disabled>
            Seçiniz
          </option>
          {[1, 2, 3, 4, 5, 6].map((g) => (
            <option key={g} value={g}>
              {g}. Sınıf
            </option>
          ))}
        </select>
      </div>
      <div className='mb-4'>
        <button className='btn btn-primary me-2' onClick={loadSchedule}>
          Listele
        </button>
        {schedule.length > 0 && (
          <button
            className='btn btn-secondary'
            onClick={() =>
              navigate(
                `/program/print/${selectedSemester}/${selectedDepartment}/${selectedGrade}`
              )
            }
          >
            Yazdır
          </button>
        )}
      </div>
      {renderTable()}
    </div>
  )
}

export default PublicUniversityCourseScheduleList

